import type { SimulationLinkDatum, SimulationNodeDatum } from 'd3-force';
import { forceCenter, forceCollide, forceLink, forceManyBody, forceSimulation } from 'd3-force';
import { useEffect, useRef } from 'react';

import type { GraphNode, WikiGraph } from '@/api/client';
import { MAP, MAP_PREVIEW } from '@/config';
import { useT } from '@/i18n/LocaleProvider';
import { categoryColor } from './colors';
import { useWikiGraph } from './useWikiGraph';

type SimNode = SimulationNodeDatum & GraphNode & { radius: number };
type SimLink = SimulationLinkDatum<SimNode>;

function nodeRadius(node: GraphNode): number {
  const linkCount = node.inbound_count + node.outbound_count;
  return Math.max(
    MAP.minRadius,
    Math.min(MAP.maxRadius, MAP.minRadius + linkCount * MAP.radiusScale),
  );
}

function topNodes(nodes: GraphNode[], cap: number): GraphNode[] {
  return [...nodes]
    .sort((a, b) => b.inbound_count + b.outbound_count - (a.inbound_count + a.outbound_count))
    .slice(0, cap);
}

type PreviewCanvasProps = {
  graph: WikiGraph;
};

function PreviewCanvas({ graph }: PreviewCanvasProps) {
  const t = useT();
  const ariaLabel = t('map_preview_canvas_label');
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rootStyle = getComputedStyle(document.documentElement);
    const ink = rootStyle.getPropertyValue('--ink').trim() || MAP.fallbackColors.ink;
    const muted = rootStyle.getPropertyValue('--muted').trim() || MAP.fallbackColors.muted;
    const crimson = rootStyle.getPropertyValue('--crimson').trim() || MAP.fallbackColors.crimson;

    const w = MAP_PREVIEW.width;
    const h = MAP_PREVIEW.height;
    canvas.width = w;
    canvas.height = h;

    const capped = topNodes(graph.nodes, MAP_PREVIEW.nodeCount);
    const cappedIds = new Set(capped.map((n) => n.id));

    const nodes: SimNode[] = capped.map((n) => ({ ...n, radius: nodeRadius(n) }));
    const links: SimLink[] = graph.edges
      .filter((e) => cappedIds.has(e.from) && cappedIds.has(e.to))
      .map((e) => ({ source: e.from, target: e.to }));

    const sim = forceSimulation<SimNode>(nodes)
      .force(
        'link',
        forceLink<SimNode, SimLink>(links)
          .id((d) => d.id)
          .distance(MAP.linkDistance * 0.6),
      )
      .force('charge', forceManyBody<SimNode>().strength(MAP.charge * 0.7))
      .force('center', forceCenter<SimNode>(w / 2, h / 2))
      .force(
        'collide',
        forceCollide<SimNode>((d) => d.radius + MAP.collideGap),
      )
      .alphaDecay(MAP_PREVIEW.alphaDecay)
      .stop();

    sim.tick(MAP_PREVIEW.tickCount);

    ctx.clearRect(0, 0, w, h);

    // Edges
    ctx.beginPath();
    ctx.strokeStyle = muted;
    ctx.lineWidth = 0.8;
    ctx.globalAlpha = 0.3;
    for (const link of links) {
      const s = link.source;
      const t = link.target;
      if (typeof s !== 'object' || s === null || typeof t !== 'object' || t === null) continue;
      if (s.x == null || s.y == null || t.x == null || t.y == null) continue;
      ctx.moveTo(s.x, s.y);
      ctx.lineTo(t.x, t.y);
    }
    ctx.stroke();

    // Nodes
    ctx.globalAlpha = 1;
    for (const node of nodes) {
      if (node.x == null || node.y == null) continue;
      const r = Math.max(3, node.radius * 0.7);
      ctx.beginPath();
      ctx.arc(node.x, node.y, r, 0, Math.PI * 2);
      if (node.missing || node.stub) {
        ctx.setLineDash([2, 2]);
        ctx.strokeStyle = muted;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.setLineDash([]);
      } else {
        ctx.fillStyle = categoryColor(node.categories?.[0], MAP.palette) ?? crimson;
        ctx.fill();
      }
    }

    // Labels for well-connected nodes only
    ctx.font = MAP.labelFont;
    ctx.fillStyle = ink;
    ctx.globalAlpha = 0.85;
    for (const node of nodes) {
      if (node.x == null || node.y == null) continue;
      if (node.radius < MAP.labelMinRadius) continue;
      ctx.fillText(node.title, node.x + node.radius * 0.7 + 2, node.y + 4);
    }
    ctx.globalAlpha = 1;

    return () => {
      sim.stop();
    };
  }, [graph]);

  return (
    <canvas
      ref={canvasRef}
      className="map-preview-canvas"
      data-testid="map-preview-canvas"
      aria-label={ariaLabel}
      width={MAP_PREVIEW.width}
      height={MAP_PREVIEW.height}
    />
  );
}

type MapPreviewProps = {
  onNavigate?: () => void;
};

export function MapPreview({ onNavigate }: MapPreviewProps) {
  const t = useT();
  const { data, isLoading, error } = useWikiGraph();

  if (isLoading || error || !data) return null;

  return (
    <section className="map-preview" data-testid="map-preview">
      <h2 className="map-preview-title">{t('map_title')}</h2>
      <a
        href="/map"
        className="map-preview-link"
        aria-label={t('map_preview_link_label')}
        data-testid="map-preview-link"
        onClick={(e) => {
          e.preventDefault();
          onNavigate?.();
        }}
      >
        <PreviewCanvas graph={data} />
      </a>
    </section>
  );
}
